import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AlertType, IFilterInput, ReportType } from '../../graphql/schema/p4types';
import { DocumentCarousel } from './DocumentCarousel';
import SentryFallback from '../Sentry/SentryFallback';
import * as Sentry from '@sentry/react';
import setIn from '../connectedForm/utils/setIn';
import { IJobMeta } from '../../utils/BackgroundJobs';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import FluentModal from '../modals/FluentModal';

//Note - Parent may provide more complete actionEntities, but we only care about the id here.
// (Should not be printing unsaved records, or can use legacy code like DocumentModal2 if we do...).
export type IActionCriteria = { filters: Array<IFilterInput>; actionEntities: Array<{ id: number }>; include: boolean };

type IDocumentPrintContext = {
  printDocumentSingle: (
    reportType: ReportType,
    entityId: number,
    options?: { onPrintComplete?: () => void; metaData?: IPrintMetaData }
  ) => void;
  printDocumentActionEntities: (
    reportType: ReportType | SpecialReportType,
    actionCriteria: IActionCriteria,
    options?: { reprint?: boolean; onPrintComplete?: () => void; metaData?: IPrintMetaData }
  ) => void;
  onSkip: (processed?: boolean) => void; //Skip to the next document. If 'processed: true' passed as the document will be updated so we can't navigate to it again in this cycle.
  onBack?: () => void; //Go back to the previous document - Only provided if there's one to go back to.
  prevDocument?: IDocumentPrint; //Next document in the Carousel, if available.
  nextDocument?: IDocumentPrint; //Prev unprocessed document in the Carousel, if available.
};

export enum AssemblyPrintSource {
  SalesOrder = 'SalesOrder',
  Assembly = 'Assembly',
}

type IAssemblyPrintMetaData =
  | {
      source: AssemblyPrintSource.SalesOrder;
    }
  | {
      source: AssemblyPrintSource.Assembly;
    };

// "Add other MetaData types as required", i.e. would later potentially be like 'IAssemblyPrintMetaData | IPickingInstructionPrintMetaData | ....'
export type IPrintMetaData = IAssemblyPrintMetaData;

/**
 * At least one Document (Dispatch Labels) has a very custom approach to printing and has no associated "ReportType" (As there are no templates, etc).
 * This is to let us specify those and still send them via the Document Carousel.
 */
export enum SpecialReportType {
  DispatchLabel = 'DISPATCH_LABEL',
}

export type IDocumentPrint = {
  reportType: ReportType | SpecialReportType;
  reprint: boolean;
  processed: boolean;
  actionCriteria: IActionCriteria;
  onPrintComplete?: (job: IJobMeta) => void;
  metaData?: IPrintMetaData;
};

//Note - Not intended for use outside of the Document Carousel re-usable components. Use the 'useP4DocumentController' hook to access.
export const DocumentCarouselContext = React.createContext<IDocumentPrintContext>({} as any);

/**
 * To print a document, call this hook to access the functions (Or use context directly).
 */
export const useP4DocumentController = () => {
  const { printDocumentSingle, printDocumentActionEntities } = useContext(DocumentCarouselContext);
  return useMemo(
    () => ({ printDocumentSingle, printDocumentActionEntities }),
    [printDocumentSingle, printDocumentActionEntities]
  );
};

/**
 * Get the last "valid" document index (Skips over 'processed' documents).
 */
const getPrevIndex = (documents: Array<IDocumentPrint>, activeIndex: number): number | undefined => {
  let prevIndex = activeIndex;
  while (--prevIndex >= 0) {
    if (!documents[prevIndex]?.processed) {
      return prevIndex;
    }
  }
  return undefined;
};

/**
 * Get the next "valid" document index (Skips over 'processed' documents).
 */
const getNextIndex = (documents: Array<IDocumentPrint>, activeIndex: number): number | undefined => {
  let nextIndex = activeIndex;
  while (++nextIndex < documents.length) {
    if (!documents[nextIndex]?.processed) {
      return nextIndex;
    }
  }
  return undefined;
};

export default ({ children }: { children: ReactNode }) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [documents, setDocuments] = useState<Array<IDocumentPrint>>([]);
  const { dispatchSimpleToast } = useP4ToastController();

  useEffect(() => {
    //Once every document has been printed, reset the list to close the modal.
    if (documents.length > 0 && documents.every((document) => document.processed)) {
      setDocuments([]);
      dispatchSimpleToast('Documents Complete', 'All documents have been processed or skipped.', AlertType.Info);
    }
  }, [dispatchSimpleToast, documents]);

  /**
   * Skip to the next document to process, or close the Modal if all have been processed.
   * Also called after print with 'processed = true' to also update the state to indicate it's processed, this is so we
   * don't go 'back' to it later (Since it's already been completed).
   */
  const onSkip = useCallback(
    (processed = false) => {
      setActiveIndex((currentIndex) => {
        const nextIndex = getNextIndex(documents, currentIndex);
        if (nextIndex !== undefined) {
          if (processed) {
            setDocuments((currentDocuments) => {
              return setIn(currentDocuments, `${currentIndex}.processed`, true);
            });
          }
          return nextIndex;
        }
        setDocuments([]);
        return 0;
      });
    },
    [documents]
  );

  const onBack = useCallback(() => {
    setActiveIndex((currentIndex) => {
      //Should never 'fall back' to 0 because the 'back' button is hidden if there's nothing to go back to
      // - Just here in case someone manages to double-click/etc before the form re-renders.
      return getPrevIndex(documents, currentIndex) ?? 0;
    });
  }, [documents]);

  const documentCarouselContext = useMemo<IDocumentPrintContext>(() => {
    const printDocumentSingle: IDocumentPrintContext['printDocumentSingle'] = (
      reportType: ReportType,
      entityId: number,
      { onPrintComplete, metaData } = {}
    ) => {
      const documentPrint: IDocumentPrint = {
        reportType,
        processed: false,
        reprint: false,
        actionCriteria: { filters: [], actionEntities: [{ id: entityId }], include: true },
        onPrintComplete,
        metaData: metaData,
      };

      setDocuments((existingDocuments) => [...existingDocuments, documentPrint]);
    };
    const printDocumentActionEntities: IDocumentPrintContext['printDocumentActionEntities'] = (
      reportType: ReportType,
      actionCriteria: IActionCriteria,
      {
        reprint = false,
        onPrintComplete,
      }: { reprint?: boolean; onPrintComplete?: () => void; metaData?: IPrintMetaData } = {},
      metaData?: IPrintMetaData
    ) => {
      setDocuments((existingDocuments) => [
        ...existingDocuments,
        { reportType, reprint, processed: false, actionCriteria, onPrintComplete, metaData },
      ]);
    };

    const nextIndex = getNextIndex(documents, activeIndex);
    const prevIndex = getPrevIndex(documents, activeIndex);

    return {
      printDocumentSingle,
      printDocumentActionEntities,
      onSkip,
      //If at least one "skipped" document was not processed, enable the 'Back' button.
      onBack: prevIndex !== undefined ? onBack : undefined,
      prevDocument: prevIndex !== undefined ? documents[prevIndex] : undefined,
      nextDocument: nextIndex !== undefined ? documents[nextIndex] : undefined,
    };
  }, [activeIndex, documents, onBack, onSkip]);

  //Override the usual sentry fallback so that the "Go Back" button just closes the Document Modal that crashed, rather than actually navigating back.
  const sentryFallback = useCallback(({ error, componentStack }: { error: unknown; componentStack: string }) => {
    return <SentryFallback error={error} componentStack={componentStack} onClose={() => setDocuments([])} />;
  }, []);

  return (
    <DocumentCarouselContext.Provider value={documentCarouselContext}>
      {children}
      <FluentModal isOpen={documents.length > 0} styles={{ main: { minWidth: '700px', maxWidth: '800px' } }}>
        <Sentry.ErrorBoundary fallback={sentryFallback}>
          <DocumentCarousel activeIndex={activeIndex} documents={documents} />
        </Sentry.ErrorBoundary>
      </FluentModal>
    </DocumentCarouselContext.Provider>
  );
};
